import React from 'react';
import { Box, Button } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Panel, Group as PanelGroup } from 'react-resizable-panels';
import { ResizeHandle } from './ResizeHandle';
import { useViewport } from '../hooks/useViewport';

/** List width on a landscape tablet (900 to 1199): wide enough for a row's title, narrow enough to leave the detail readable. */
const TABLET_LIST_WIDTH = 280;

interface MasterDetailLayoutProps {
  list: React.ReactNode;
  detail: React.ReactNode;
  /** Whether the caller has an item open; below `md` this decides which pane is shown. */
  hasSelection: boolean;
  /** Called by the back control below `md`; the caller clears its selection. */
  onBack: () => void;
  /** List width on a desktop, in px; a resizable layout sizes its panes itself. */
  listWidth?: number;
  /** Accessible name of the back control below `md`. */
  backLabel?: string;
  /**
   * From `md` up, a draggable divider between the panes, and the detail pane only while an item is open.
   * Without it the list keeps a fixed width and the detail pane always shows.
   */
  resizable?: boolean;
}

/**
 * A list beside the detail of the item picked from it. Below `md` (phones and portrait
 * tablets) there is no room for both, so it shows one pane at a time: the list until an item is picked, then
 * that item's detail with a back control that returns to the list.
 *
 * Both modes render the same tree and only hide the pane that is out of view, so a tablet rotating across
 * `md` keeps the list's search and sort and the open detail's own state.
 */
const MasterDetailLayout: React.FC<MasterDetailLayoutProps> = ({
  list,
  detail,
  hasSelection,
  onBack,
  listWidth = 320,
  backLabel = 'Back',
  resizable = false,
}) => {
  const { isCompact, isDesktop } = useViewport();
  const listHidden = isCompact && hasSelection;
  // Below `md` with nothing open, the detail pane holds only a placeholder; it is left out rather than hidden.
  const showDetail = hasSelection || !isCompact;

  const backBar = isCompact && (
    <Box sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
      <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack} aria-label={backLabel}>
        Back
      </Button>
    </Box>
  );
  const detailPane = (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, height: '100%' }}>
      {backBar}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, overflow: 'auto' }}>
        {detail}
      </Box>
    </Box>
  );

  if (resizable) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          minWidth: 0,
          minHeight: 0,
          height: '100%',
          // The library sets each panel's display inline, so hiding a pane has to outrank it.
          ...(listHidden && {
            '& [data-pane="list"], & [data-separator]': { display: 'none !important' },
          }),
        }}
      >
        <PanelGroup orientation="horizontal" style={{ height: '100%', width: '100%', overflow: 'hidden' }}>
          <Panel minSize="30%" data-pane="list">
            <Box
              data-testid="master-detail-list"
              sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
            >
              {list}
            </Box>
          </Panel>
          {hasSelection && (
            <>
              <ResizeHandle />
              <Panel defaultSize="35%" minSize="20%" maxSize="60%">
                {detailPane}
              </Panel>
            </>
          )}
        </PanelGroup>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', minWidth: 0, minHeight: 0, height: '100%' }}>
      <Box
        data-testid="master-detail-list"
        sx={{
          display: listHidden ? 'none' : 'flex',
          flexDirection: 'column',
          minHeight: 0,
          bgcolor: 'background.default',
          ...(isCompact
            ? { flex: 1 }
            : { width: isDesktop ? listWidth : TABLET_LIST_WIDTH, flexShrink: 0, borderRight: 1, borderColor: 'divider' }),
        }}
      >
        {list}
      </Box>
      {showDetail && detailPane}
    </Box>
  );
};

export default MasterDetailLayout;
